/**
 * 選択中ワークスペースの Effective Entitlements を解決し、設定 UI の出し分けに使う。
 *
 * 判定単位は Offer（seriesId × planId）。子ワークスペースは親の Offer を継承するため、
 * parentWorkspaceId があるときだけ親ドキュメントを読んで解決し直す。
 *
 * offers/{offerId} は Rules 上 Service Admin しか読めないので、ここではコードの
 * デフォルトと workspaces/{id}.entitlementOverrides だけで解決する。Offer を DB で
 * 上書きし始めたら Functions 側の Effective とズレるため、そのときは callable 経由に切り替えること。
 */
import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useWorkspaceContext } from '../../context/WorkspaceContext';
import {
  resolveEffectiveEntitlements,
  type EffectiveEntitlements,
  type FeatureKey,
  type WorkspaceOfferFields,
} from './entitlements';
import { getFeatureAncestors } from './featureHierarchy';

/** 親ドキュメントの読み取りはワークスペース切替のたびに走るのでプロセス内で使い回す */
const parentOfferCache = new Map<string, WorkspaceOfferFields>();

async function fetchParentOffer(parentWorkspaceId: string): Promise<WorkspaceOfferFields> {
  const cached = parentOfferCache.get(parentWorkspaceId);
  if (cached) return cached;

  const snap = await getDoc(doc(db, 'workspaces', parentWorkspaceId));
  const data = snap.exists() ? (snap.data() as Record<string, unknown>) : {};
  const offer: WorkspaceOfferFields = {
    seriesId: data.seriesId,
    planId: data.planId,
    offerId: data.offerId,
  };
  parentOfferCache.set(parentWorkspaceId, offer);
  return offer;
}

export function invalidateParentOfferCache(): void {
  parentOfferCache.clear();
}

export interface WorkspaceEntitlements {
  /** 自身と全祖先が enabled のときだけ true */
  can: (feature: FeatureKey) => boolean;
  /** ワークスペース未選択、または親ワークスペースの解決待ち */
  loading: boolean;
  effective: EffectiveEntitlements | null;
}

export function useWorkspaceEntitlements(): WorkspaceEntitlements {
  const { selectedWorkspace, loading: workspaceLoading } = useWorkspaceContext();
  const parentWorkspaceId = selectedWorkspace?.parentWorkspaceId;

  const [parentOffer, setParentOffer] = useState<WorkspaceOfferFields | null>(null);

  useEffect(() => {
    if (!parentWorkspaceId) {
      setParentOffer(null);
      return;
    }

    let cancelled = false;
    fetchParentOffer(parentWorkspaceId)
      .then((offer) => {
        if (!cancelled) setParentOffer(offer);
      })
      .catch((error) => {
        console.error('[useWorkspaceEntitlements] 親ワークスペースの取得に失敗しました', {
          parentWorkspaceId,
          error,
        });
        if (!cancelled) setParentOffer(null);
      });

    return () => {
      cancelled = true;
    };
  }, [parentWorkspaceId]);

  const effective = useMemo<EffectiveEntitlements | null>(() => {
    if (!selectedWorkspace) return null;
    return resolveEffectiveEntitlements({
      seriesId: selectedWorkspace.seriesId,
      planId: selectedWorkspace.planId,
      offerId: selectedWorkspace.offerId,
      parentWorkspaceId: selectedWorkspace.parentWorkspaceId,
      parent: parentOffer ?? undefined,
      override: selectedWorkspace.entitlementOverrides,
    });
  }, [selectedWorkspace, parentOffer]);

  // 親の解決待ちのあいだは子自身の Offer で暫定表示する。作成時に親の planId を
  // コピーしているため通常は同じ値になり、ちらつきを避けられる
  const loading = workspaceLoading || !selectedWorkspace || (!!parentWorkspaceId && !parentOffer);

  const can = useMemo(() => {
    return (feature: FeatureKey): boolean => {
      if (!effective) return false;
      if (!effective.features[feature]?.enabled) return false;
      return getFeatureAncestors(feature).every(
        (ancestor) => effective.features[ancestor]?.enabled !== false
      );
    };
  }, [effective]);

  return { can, loading, effective };
}
